import {
	createFileRoute,
	useLocation,
	useNavigate,
} from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { FullPageSpinner } from "@/components/ui/full-page-spinner";
import { useOrgContext } from "@/hooks/use-org-context";
import { fetchInstallableConnectorCatalog } from "@/lib/api";
import { resolveAppInstallTarget } from "@/lib/onboarding-install";
import { signInSearch } from "@/lib/sign-in-redirect";

export const Route = createFileRoute("/")({ component: HomePage });

function HomePage() {
	const { isAuthenticated, authReady, currentOwner, orgsReady } =
		useOrgContext();
	const navigate = useNavigate();
	const location = useLocation();
	const redirectStarted = useRef(false);

	// A brand-new social signup is routed here by better-auth's
	// `newUserCallbackURL` (set in login.tsx), carrying the signup provider on
	// the query string. better-auth only sends genuine new users here —
	// returning users hit `callbackURL` — so no server-stored intent is needed;
	// we resolve which connector to install from the connector catalog (scoped
	// to the owner slug, which this slug-less route doesn't carry in its URL),
	// then jump into the gateway's signed install start-route.
	const onboardProvider = new URLSearchParams(window.location.search).get(
		"onboard_provider",
	);

	// A public connector link can name a connector before the visitor has a
	// workspace. Resolve the owner after sign-in; the connector page keeps
	// responsibility for catalog discovery, access checks, and setup.
	const connectorParam = new URLSearchParams(window.location.search).get(
		"connector",
	);
	const requestedConnector =
		connectorParam &&
		/^[a-z0-9][a-z0-9._-]{0,127}$/.test(connectorParam) &&
		!["create", "deployments"].includes(connectorParam)
			? connectorParam
			: null;

	useEffect(() => {
		if (!authReady) return;

		// This is a redirect-only home route. The responsive shell's narrow
		// container-query flip (false→true after mount) can transiently
		// re-render / remount this component while a redirect is still in
		// flight. A stale instance still resolves `currentOwner` from the new
		// URL and would re-issue `navigate({ to: "/$owner" })` even though the
		// URL has already moved on — which ping-pongs with `$owner`'s
		// beforeLoad redirect into an infinite navigation loop
		// at narrow widths (the embedded side panel pegged on a spinner).
		// Only redirect while this route is actually active (URL is "/").
		if (location.pathname !== "/") return;

		if (isAuthenticated && currentOwner) {
			// Chrome extension deep links: the side-panel bootstrap lands here with
			// a hash because the extension knows device/run/agent ids but not the
			// org slug. We resolve `currentOwner` and forward.
			//   `#worker=<workerId>`              → Connectors › device detail (matched by worker_id)
			//   `#agent=&thread=&message=`        → Agent chat (+ optional scroll)
			//   `#run=<runId>`                    → Memory for that run + peek pane
			//
			// CRITICAL (sidepanel): do NOT re-fire router.navigate while still on
			// `/` with the same hash — TanStack load + remount loops hit React
			// #185 (max update depth) and leave the iframe blank. Clear the hash
			// and hard-replace once so a narrow embedded remount cannot re-enter.
			const hashParams = new URLSearchParams(
				window.location.hash.replace(/^#/, ""),
			);
			const agentHash = hashParams.get("agent")?.trim();
			const threadHash = hashParams.get("thread")?.trim();
			const messageHash =
				hashParams.get("message")?.trim() ||
				hashParams.get("message_id")?.trim();
			const runIdRaw = hashParams.get("run")?.trim();
			const deviceId = hashParams.get("worker")?.trim();
			const hasDeepLink =
				(!!agentHash && !!threadHash) ||
				(!!runIdRaw && /^\d+$/.test(runIdRaw)) ||
				!!deviceId;

			if (hasDeepLink) {
				if (redirectStarted.current) return;
				redirectStarted.current = true;
				// Drop the fragment before navigating so a remount of `/` cannot
				// re-read agent/thread/run and navigate again.
				window.history.replaceState(
					null,
					"",
					`${window.location.pathname}${window.location.search}`,
				);

				if (agentHash && threadHash) {
					const qs = messageHash
						? `?message_id=${encodeURIComponent(messageHash)}`
						: "";
					window.location.replace(
						`/${encodeURIComponent(currentOwner)}/chat/${encodeURIComponent(agentHash)}/c/${encodeURIComponent(threadHash)}${qs}`,
					);
					return;
				}
				if (runIdRaw && /^\d+$/.test(runIdRaw)) {
					const qs = new URLSearchParams({
						run_ids: runIdRaw,
						peek: "1",
						peek_run_id: runIdRaw,
					});
					window.location.replace(
						`/${encodeURIComponent(currentOwner)}/events?${qs.toString()}`,
					);
					return;
				}
				if (deviceId) {
					window.location.replace(
						`/${encodeURIComponent(currentOwner)}/connectors/device/${encodeURIComponent(deviceId)}`,
					);
					return;
				}
			}

			if (redirectStarted.current) return;
			redirectStarted.current = true;

			if (requestedConnector) {
				void navigate({
					to: "/$owner/connectors/$connectorKey",
					params: { owner: currentOwner, connectorKey: requestedConnector },
					replace: true,
				});
				return;
			}

			const goToDashboard = () =>
				void navigate({
					to: "/$owner",
					params: { owner: currentOwner },
					replace: true,
				});

			// New social signup → its matching app-install connector. Fetch the
			// catalog scoped to this owner, then show verified connection options.
			// Anything that does not resolve to
			// a real install target (no match, fetch error) falls through to the
			// normal dashboard — never strand on the spinner.
			if (onboardProvider) {
				void fetchInstallableConnectorCatalog(currentOwner)
					.then((connectors) => {
						const target = resolveAppInstallTarget(connectors, onboardProvider);
						if (target) {
							window.location.assign(
								`/${encodeURIComponent(currentOwner)}/connectors/${encodeURIComponent(target.connectorKey)}`,
							);
							return;
						}
						goToDashboard();
					})
					.catch(goToDashboard);
				return;
			}

			goToDashboard();
			return;
		}

		if (isAuthenticated) {
			// currentOwner is null. Distinguish "org list still loading" from
			// "genuinely belongs to no org". Without a synchronous personalOrgSlug
			// (session.user.username), currentOwner only resolves once
			// `/api/organizations` settles. Redirecting to settings before then
			// (with replace) unmounts this route and strands the user, because the
			// org list arrives too late to re-trigger this effect. Wait for
			// orgsReady so users with a personal org land on it.
			if (orgsReady) {
				void navigate({ to: "/account/settings", replace: true });
			}
			return;
		}

		void navigate({
			to: "/auth/login",
			search: signInSearch({
				callbackUrl: requestedConnector
					? `/?connector=${encodeURIComponent(requestedConnector)}`
					: undefined,
			}),
			replace: true,
		});
	}, [
		authReady,
		currentOwner,
		isAuthenticated,
		orgsReady,
		navigate,
		location.pathname,
		onboardProvider,
		requestedConnector,
	]);

	return (
		<div data-public-loading="true">
			<FullPageSpinner delayMs={0} className="min-h-0 py-16" />
		</div>
	);
}
